import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Anchor, Home, Users, BookOpen, MapPin, Wallet, ChevronRight, Store } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";

const STORE_ICONS = { rema: "🛒", kiwi: "🛒", extra: "🛒", coop: "🛒", meny: "🛒" };
const MODE_LABEL = { smart: "⚡ Smart", relaxed: "💰 Romslig" };

export default function AdminOverview() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.getAdminOverview();
        if (!cancelled) setData(res.data);
      } catch (err) {
        toast.error(err.response?.data?.detail || "Kunne ikke laste admin-oversikt");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const activate = async (boatId) => {
    try {
      await api.activateBoat(boatId);
      toast.success("Aktivert – omdirigerer...");
      setTimeout(() => window.location.reload(), 400);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke aktivere");
    }
  };

  if (user?.role !== "admin") {
    return <div className="p-6 text-center text-sm text-stone-500">Kun admin har tilgang til denne siden.</div>;
  }
  if (loading) return <div className="p-6 text-center text-sm text-stone-400">Laster admin-oversikt...</div>;
  if (!data) return null;

  const { totals, boats, households } = data;
  const showBoats = tab === "all" || tab === "boats";
  const showHouseholds = tab === "all" || tab === "households";

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto" data-testid="admin-overview-page">
      <div className="mb-6">
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-stone-900" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Admin-oversikt</h1>
        <p className="text-sm text-stone-500 mt-1">Alle båter og husstander på tvers av alle brukere.</p>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="admin-totals">
        <div className="bg-navy-800 text-white rounded-xl p-4" data-testid="total-boats">
          <Anchor className="h-5 w-5 mb-2 opacity-80" />
          <div className="text-3xl font-bold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{totals.boats}</div>
          <div className="text-[11px] uppercase tracking-wider opacity-80 font-semibold">Båter</div>
        </div>
        <div className="bg-gradient-to-br from-amber-600 to-orange-600 text-white rounded-xl p-4" data-testid="total-households">
          <Home className="h-5 w-5 mb-2 opacity-90" />
          <div className="text-3xl font-bold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{totals.households}</div>
          <div className="text-[11px] uppercase tracking-wider opacity-90 font-semibold">Husstander</div>
        </div>
        <div className="bg-white border border-stone-100 rounded-xl p-4" data-testid="total-users">
          <Users className="h-5 w-5 mb-2 text-emerald-600" />
          <div className="text-3xl font-bold text-stone-800" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{totals.users}</div>
          <div className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">Brukere</div>
        </div>
        <div className="bg-white border border-stone-100 rounded-xl p-4" data-testid="total-recipes">
          <BookOpen className="h-5 w-5 mb-2 text-red-600" />
          <div className="text-3xl font-bold text-stone-800" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{totals.recipes}</div>
          <div className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">Oppskrifter</div>
        </div>
      </div>

      {/* Tab filter */}
      <div className="flex gap-2 mb-4" data-testid="overview-tabs">
        {[
          { id: "all", label: "Alle" },
          { id: "boats", label: `Båter (${boats.length})` },
          { id: "households", label: `Husstander (${households.length})` },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${tab === t.id ? "bg-navy-800 text-white" : "bg-white border border-stone-200 text-stone-500 hover:border-stone-300"}`}
            data-testid={`tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {showBoats && boats.length > 0 && (
        <section className="mb-8" data-testid="boats-section">
          <h2 className="text-lg font-semibold text-stone-700 mb-3 flex items-center gap-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            <Anchor className="h-4 w-4 text-navy-800" /> Båter
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {boats.map((b) => (
              <div key={b.id} className="bg-white border border-stone-100 rounded-xl p-4" data-testid={`boat-card-${b.id}`}>
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex items-center gap-2 min-w-0">
                    {b.image_url ? (
                      <img src={b.image_url} alt="" className="h-10 w-10 rounded-lg object-cover border border-stone-100 flex-shrink-0" />
                    ) : (
                      <div className="h-10 w-10 rounded-lg bg-navy-50 flex items-center justify-center flex-shrink-0"><Anchor className="h-4 w-4 text-navy-800" /></div>
                    )}
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-stone-800 truncate">{b.name}</div>
                      <div className="text-xs text-stone-400 truncate">Eier: {b.owner_name}</div>
                    </div>
                  </div>
                  <button
                    onClick={() => activate(b.id)}
                    className="flex-shrink-0 text-[11px] font-semibold text-navy-800 hover:bg-navy-50 rounded-md px-2 py-1 inline-flex items-center gap-1"
                    data-testid={`activate-${b.id}`}
                  >
                    Åpne <ChevronRight className="h-3 w-3" />
                  </button>
                </div>
                <div className="flex flex-wrap gap-2 text-xs text-stone-500">
                  <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" /> {b.member_count} medlemmer</span>
                  <span>· {b.num_onboard || 0} om bord</span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {showHouseholds && households.length > 0 && (
        <section data-testid="households-section">
          <h2 className="text-lg font-semibold text-stone-700 mb-3 flex items-center gap-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            <Home className="h-4 w-4 text-amber-700" /> Husstander
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {households.map((h) => (
              <div key={h.id} className="bg-white border border-stone-100 rounded-xl p-4" data-testid={`household-card-${h.id}`}>
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="h-10 w-10 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0"><Home className="h-4 w-4 text-amber-700" /></div>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-stone-800 truncate">{h.name}</div>
                      <div className="text-xs text-stone-400 truncate">Eier: {h.owner_name}</div>
                    </div>
                  </div>
                  <button
                    onClick={() => activate(h.id)}
                    className="flex-shrink-0 text-[11px] font-semibold text-amber-700 hover:bg-amber-50 rounded-md px-2 py-1 inline-flex items-center gap-1"
                    data-testid={`activate-${h.id}`}
                  >
                    Åpne <ChevronRight className="h-3 w-3" />
                  </button>
                </div>
                {h.address && (
                  <div className="flex items-center gap-1 text-xs text-stone-500 mb-1.5"><MapPin className="h-3 w-3" /> {h.address}</div>
                )}
                <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-stone-500">
                  <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" /> {h.member_count}</span>
                  <span>{h.household_days || 7} dager</span>
                  {h.monthly_budget_nok > 0 && (
                    <span className="inline-flex items-center gap-1"><Wallet className="h-3 w-3 text-emerald-600" /> {h.monthly_budget_nok} kr/mnd <span className="text-[10px] text-stone-400">{MODE_LABEL[h.budget_mode] || ""}</span></span>
                  )}
                  <span className="inline-flex items-center gap-1"><Store className="h-3 w-3" /> {STORE_ICONS[h.preferred_store] || ""} {h.preferred_store}</span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {tab === "boats" && boats.length === 0 && <div className="text-sm text-stone-400 text-center py-8">Ingen båter registrert ennå.</div>}
      {tab === "households" && households.length === 0 && <div className="text-sm text-stone-400 text-center py-8">Ingen husstander registrert ennå.</div>}
    </div>
  );
}
